// What's new on the web (owner, 2026-10-04: "when updates happen, there should be a 'what's new'
// modal that comes up"). The entries and their rules are shared/whats-new.ts; this module keeps the
// device's seen set (the ids it has been shown) and decides, once per page load, what the sheet
// (src/components/WhatsNew.tsx) may say.
//
// The rule: a brand new device (no record, and the tour has shown it nothing) marks every entry seen
// on its first signed-in open, visible or not, and shows nothing, since everything is new to it. A
// device in use before What's new existed (no record, but a tour seen or a name chosen) starts from
// an empty set. Any other shows the entries it has not been shown whose flag is on (owner,
// 2026-10-05: "OTA updates should also have what's new entries for new features. Visibility
// dependent on global feature flags"), once the board has loaded, in one sheet; closing the sheet
// marks the visible ones seen, and one hidden by its flag stays unseen until it shows. The tour
// wins: the app shell holds the sheet while a tour shows or the first open's welcome is still to
// come, and shows it when the tour closes, in the same open (src/app.tsx). It comes before the
// season recap and the install and notification prompts.
import { useEffect, useState } from "preact/hooks";
import {
  WHATS_NEW,
  WHATS_NEW_BOOKMARK_KEY,
  WHATS_NEW_SEEN_IDS_KEY,
  allWhatsNewIds,
  bookmarkForSeen,
  orderWhatsNew,
  readSeenIds,
  serializeSeenIds,
  visibleWhatsNew,
  type WhatsNewEntry,
  type WhatsNewFlags,
} from "../shared/whats-new.ts";
import { HOUSEHOLD_EVENT, LOCAL_FLAGS, currentFlags } from "./flags-core.ts";
import { navigate, type Route } from "./router.ts";

/** Where an entry's link goes (owner, 2026-10-04: "There can be deep links in the what's new"). */
export type WhatsNewLinkRoute = NonNullable<WhatsNewEntry["link"]>["route"];

/** The ids this device has been shown, a JSON array (shared/whats-new.ts). */
export const WHATS_NEW_KEY = WHATS_NEW_SEEN_IDS_KEY;
/** The old bookmark, migrated from on first read and still written for one release (shared/whats-new.ts). */
export const WHATS_NEW_OLD_KEY = WHATS_NEW_BOOKMARK_KEY;

/** Kept when storage refuses, so the sheet is not shown twice in one open of a private window. */
let memory: Set<number> | null = null;

/** The flags an entry's visibility reads: this device's effective flags (house or local), as every feature's UI reads them. */
let readFlags: () => WhatsNewFlags = currentFlags;

/**
 * The device's seen set: the stored ids, else the old bookmark migrated (every id at or below it
 * counts as seen), else null for a device with no record (or one holding something unreadable).
 */
export function whatsNewSeen(): Set<number> | null {
  try {
    return readSeenIds(localStorage.getItem(WHATS_NEW_KEY), localStorage.getItem(WHATS_NEW_OLD_KEY));
  } catch {
    return memory;
  }
}

/**
 * Adds `ids` to the seen set (never removes one). Also writes the old bookmark, the highest id seen,
 * for one release, so a device that goes back to a build before the seen set behaves.
 */
export function markWhatsNewSeen(ids: readonly number[]): void {
  const next = new Set(whatsNewSeen() ?? []);
  for (const id of ids) next.add(id);
  memory = next;
  try {
    localStorage.setItem(WHATS_NEW_KEY, serializeSeenIds(next));
    // One release only (2026-10-05): drop with WHATS_NEW_BOOKMARK_KEY.
    localStorage.setItem(WHATS_NEW_OLD_KEY, bookmarkForSeen(next));
  } catch {
    /* ignore: the copy in memory holds for this open */
  }
}

/**
 * A device that was in use before What's new existed: it has no record, but the tour has shown it
 * something (`op.tourSeen` non-empty, or the old first run's `op.tutorialDone`). Read raw, so the
 * check never seeds or writes the tour's list (src/tutorial-state.ts).
 */
export function existingDevice(): boolean {
  try {
    // A name was chosen on this device before this open: it was in use (the surest sign; the tour
    // keys below cover a device that never got as far as a name).
    if ((localStorage.getItem("op.peonName") ?? "").trim() !== "") return true;
    if (localStorage.getItem("op.tutorialDone") === "1") return true;
    const raw = localStorage.getItem("op.tourSeen");
    const list = raw ? (JSON.parse(raw) as unknown) : null;
    return Array.isArray(list) && list.length > 0;
  } catch {
    return false;
  }
}

/**
 * What an open does with the seen set it found: a device with none that was already in use starts
 * from an empty set; a brand new device (no record, no tour shown) marks every id seen now, visible
 * or not, and shows nothing; any other may show the entries not in its set (`unseen`, in WHATS_NEW
 * order, flags not yet applied: pendingWhatsNew applies them when the sheet may show).
 */
export function whatsNewOnOpen(seen: ReadonlySet<number> | null, existing = false): { unseen: WhatsNewEntry[]; markNow: number[] | null } {
  if (seen === null && existing) seen = new Set();
  if (seen === null) return { unseen: [], markNow: allWhatsNewIds() };
  const known = seen;
  return { unseen: WHATS_NEW.filter((e) => !known.has(e.id)), markNow: null };
}

/**
 * Whether the sheet shows now: it has entries, the board has loaded and nothing holds it (a tour
 * showing, the first open's welcome still to come, the workshop open). The tour always wins.
 */
export function whatsNewShows(entries: readonly WhatsNewEntry[], boardLoaded: boolean, held: boolean): boolean {
  return entries.length > 0 && boardLoaded && !held;
}

// ---------- this page load ----------

/** The unseen entries decided at the first signed-in open, flags not yet applied; null until then. */
let entries: WhatsNewEntry[] | null = null;
let boardLoaded = false;
let closed = false;
const listeners = new Set<() => void>();

function emit(): void {
  for (const fn of listeners) fn();
}

/** The app is signed in: decide, once per page load, what this open has to say. */
export function startWhatsNew(): void {
  if (entries !== null) return;
  const { unseen, markNow } = whatsNewOnOpen(whatsNewSeen(), existingDevice());
  if (markNow !== null) markWhatsNewSeen(markNow);
  entries = unseen;
  emit();
}

/** The open board has its list (src/views/Board.tsx): the sheet may show from now on. */
export function whatsNewBoardLoaded(): void {
  if (boardLoaded) return;
  boardLoaded = true;
  emit();
}

/** The unseen entries whose flag is on now, in WHATS_NEW order (before the Other notes collapse). */
function visibleUnseen(): WhatsNewEntry[] {
  return entries ? visibleWhatsNew(entries, readFlags()) : [];
}

/**
 * The entries waiting to be shown this open, once the board has loaded, in the sheet's order
 * (features newest first, one Other note last), each only while its flag is on; empty otherwise.
 */
export function pendingWhatsNew(): WhatsNewEntry[] {
  return entries && boardLoaded && !closed ? orderWhatsNew(visibleUnseen()) : [];
}

/**
 * The sheet was closed (Got it, the dim, Escape): the entries it showed (every visible unseen one,
 * the older Other notes it folded in included) are marked seen, and it does not show again this
 * open. One hidden by its flag stays unseen.
 */
export function closeWhatsNew(): void {
  if (closed) return;
  closed = true;
  markWhatsNewSeen(visibleUnseen().map((e) => e.id));
  emit();
}

/**
 * Profile's About subscreen (src/components/AccountSheet.tsx) was opened: it lists every visible
 * entry, so those are marked seen as the automatic sheet's close does, and the automatic sheet has
 * nothing left to say this open. Entries hidden by their flag stay unseen.
 */
export function closeWhatsNewFromRow(): void {
  markWhatsNewSeen(visibleWhatsNew(WHATS_NEW, readFlags()).map((e) => e.id));
  closeWhatsNew();
}

/** A link's screen as a route: the names are the router's own (#/peon, #/rewards, #/upgrades, #/, #/done, #/trinkets, #/feedback). */
export function whatsNewRoute(route: WhatsNewLinkRoute): Route {
  switch (route) {
    case "peon":
      return { name: "peon" };
    case "rewards":
      return { name: "rewards" };
    case "upgrades":
      return { name: "upgrades" };
    case "done":
      return { name: "done" };
    case "open":
      return { name: "open" };
    case "trinkets":
      return { name: "trinkets" };
    case "feedback":
      return { name: "feedback" };
  }
}

/**
 * A link under an entry was followed: the sheet closes the way Got it closes it (`onClose`, which
 * marks what it showed seen), whatever it was opened over closes too (`onLink`: the account sheet, when
 * the link was followed from its About subscreen), and the app goes to the link's screen.
 */
export function followWhatsNewLink(route: WhatsNewLinkRoute, onClose: () => void, onLink?: () => void, go: (r: Route) => void = navigate): void {
  onClose();
  onLink?.();
  go(whatsNewRoute(route));
}

/**
 * The entries the shell may show (it still holds them while a tour shows), following the flags: a
 * switch turned on or off here or on another device adds or removes its entry while the sheet waits.
 */
export function useWhatsNew(): WhatsNewEntry[] {
  const [state, setState] = useState(pendingWhatsNew);
  useEffect(() => {
    const update = () =>
      setState((prev) => {
        const next = pendingWhatsNew();
        return sameIds(prev, next) ? prev : next;
      });
    listeners.add(update);
    const flagEvents = [HOUSEHOLD_EVENT, ...Object.values(LOCAL_FLAGS).map((f) => f.event)];
    for (const name of flagEvents) window.addEventListener(name, update);
    update();
    return () => {
      listeners.delete(update);
      for (const name of flagEvents) window.removeEventListener(name, update);
    };
  }, []);
  return state;
}

function sameIds(a: readonly WhatsNewEntry[], b: readonly WhatsNewEntry[]): boolean {
  return a.length === b.length && a.every((e, i) => e.id === b[i].id);
}

/** Tests only: a fresh page load, reading `flags` (every flag off unless given). */
export function resetWhatsNewForTests(flags: () => WhatsNewFlags = () => ({})): void {
  entries = null;
  boardLoaded = false;
  closed = false;
  memory = null;
  readFlags = flags;
  listeners.clear();
}
